import { MapPin, Calendar, FileText, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

import DeterminationBadge from "../../components/DeterminationBadge";
import LegendCard from "../../components/LegendCard";
import { STATUS_ICON, STATUS_COLOR } from "../../lib/constants";

const CARD_INITIAL = { opacity: 0, y: 12 };
const CARD_ANIMATE = { opacity: 1, y: 0 };
const CARD_EXIT = { opacity: 0, y: -8 };
const CARD_TRANSITION = { duration: 0.35 };

function Field({ icon: Icon, label, value, testId }) {
  if (!value) return null;
  return (
    <div className="flex gap-3" data-testid={testId}>
      <Icon className="h-4 w-4 mt-0.5 text-[#8A9A86] shrink-0" strokeWidth={1.5} />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E]">{label}</div>
        <div className="text-sm text-[#111812] break-words">{value}</div>
      </div>
    </div>
  );
}

function ResultHeader({ result }) {
  const StatusIcon = STATUS_ICON[result.status] || null;
  const statusColor = STATUS_COLOR[result.status] || "text-[#5B695E]";
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#5B695E]">Status</div>
        <div data-testid="lookup-status" className={`mt-1 inline-flex items-center gap-2 ${statusColor}`}>
          {StatusIcon && <StatusIcon className="h-5 w-5" strokeWidth={1.5} />}
          <span className="font-heading text-2xl tracking-tight text-[#111812]">
            {result.status.replace("_", " ")}
          </span>
        </div>
        <div className="mt-2 font-mono text-sm text-[#5B695E]">
          {result.normalized_parcel_id || result.parcel_id}
        </div>
      </div>
      {result.determination_code && (
        <DeterminationBadge code={result.determination_code} label={result.determination_label} />
      )}
    </div>
  );
}

function ResultFields({ result }) {
  return (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
      <Field icon={MapPin} label="Address / Unit" value={result.address} testId="field-address" />
      <Field icon={Calendar} label="Date determined" value={result.date} testId="field-date" />
      <Field icon={FileText} label="PDF page" value={result.page ? `Page ${result.page}` : null} testId="field-page" />
      <Field icon={Calendar} label="DEP last modified" value={result.pdf_last_modified} testId="field-last-modified" />
    </div>
  );
}

function RangeSidebar({ rangeSlug }) {
  return (
    <div
      data-testid="range-card"
      className="bg-white border border-border/60 rounded-2xl shadow-sm p-6"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E] mb-2">
        Matched range
      </div>
      <div className="font-heading text-2xl tracking-tight">{rangeSlug || "—"}</div>
      <div className="mt-4 text-xs text-[#5B695E]">
        First 4 digits of the parcel ID map to this DEP dataset.
      </div>
    </div>
  );
}

export default function LookupResultCard({ result }) {
  return (
    <motion.div
      key={result.id}
      initial={CARD_INITIAL}
      animate={CARD_ANIMATE}
      exit={CARD_EXIT}
      transition={CARD_TRANSITION}
      className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6"
    >
      <div
        data-testid="lookup-result-card"
        className="lg:col-span-2 bg-white border border-border/60 rounded-2xl shadow-sm p-6 sm:p-8"
      >
        <ResultHeader result={result} />

        {result.message && result.status !== "FOUND" && (
          <p className="mt-6 text-sm text-[#5B695E]" data-testid="lookup-message">
            {result.message}
          </p>
        )}

        <ResultFields result={result} />

        {result.raw_line && (
          <div className="mt-8">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5B695E] mb-2">
              Source line
            </div>
            <pre
              data-testid="field-raw-line"
              className="bg-[#F4F5F4] border border-border/60 rounded-lg px-4 py-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all"
            >
              {result.raw_line}
            </pre>
          </div>
        )}

        {result.pdf_url && (
          <a
            href={result.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="source-pdf-link"
            className="mt-8 inline-flex items-center gap-2 text-sm text-[#1A3626] hover:text-[#2D4A3A] transition-colors"
          >
            <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
            Open source PDF ({result.pdf_filename})
          </a>
        )}
      </div>

      <div className="space-y-6">
        <RangeSidebar rangeSlug={result.range_slug} />
        <LegendCard />
      </div>
    </motion.div>
  );
}
